import LegalPageLayout from '../../components/legal/LegalPageLayout'

export default function CompliancePage() {
  return (
    <LegalPageLayout
      title="Compliance"
      subtitle="SynthetIQ Signals®, Inc."
      effectiveDate="To be set upon legal review — not yet in effect"
      lastUpdated="2026-09-25 (first-pass draft)"
      bannerText="First-pass draft — not reviewed by legal counsel, not approved, not in effect. Every status below is a description of current practice or design intent, not a claim of independent certification unless explicitly stated as one. Do not cite any framework name on this page as a confirmed certification without first verifying it."
      relatedLinks={[
        { to: '/corporate/legal/privacy', label: 'Privacy Policy' },
        { to: '/corporate/legal/cookies', label: 'Cookies Policy' },
        { to: '/corporate/legal/terms', label: 'Terms of Service' },
      ]}
      intro={[
        'This page describes the security and regulatory frameworks that inform how SynthetIQ Signals®, Inc. designs and operates the Site, the 23Controls™ platform, and Graffiti™ OS. Where we have not completed an independent audit or formal certification, we say so directly rather than implying one exists — a claimed certification that isn\'t real is an FTC-enforceable false statement, the same risk already flagged on the Privacy Policy page for the Data Privacy Framework claim.',
      ]}
      sections={[
        {
          heading: 'Framework and Certification Status',
          table: {
            headers: ['Area', 'Status'],
            rows: [
              [
                'SOC 2',
                'Security practices are designed with SOC 2 Trust Services Criteria in mind (access control, encryption, monitoring, incident response). No independent SOC 2 Type I or Type II audit has been completed or should be cited as complete.',
              ],
              [
                'HIPAA (EpisodIQ)',
                'Administrative and technical safeguards for health-related data processed through EpisodIQ are designed with HIPAA requirements in mind. A formal HIPAA compliance program (risk assessments, Business Associate Agreements) has not been independently verified as part of this draft — confirm current status before citing HIPAA compliance to a healthcare prospect or partner.',
              ],
              [
                'PCI-DSS',
                'Payment card data is processed by Stripe, a PCI-DSS Level 1 certified payment processor; full card numbers are not stored on our systems (see Privacy Policy §2.1, Terms §5). This reduces our own PCI scope, but SynthetIQ Signals®, Inc.\'s own PCI self-assessment status has not been separately confirmed in this draft.',
              ],
              [
                'GDPR / UK GDPR',
                'Our data-processing practices are designed to align with GDPR principles and data-subject rights — see the Privacy Policy for the specific legal bases and rights we describe. GDPR is a legal obligation, not a certification; no formal third-party GDPR audit has been completed.',
              ],
              [
                'CCPA / CPRA',
                'California residents\' rights (know, delete, correct, opt out of sale/sharing) are described in the Privacy Policy. As with GDPR, this is a legal-compliance posture, not a certification.',
              ],
              [
                'EU-U.S. / UK / Swiss Data Privacy Framework',
                'Unverified — flagged, not confirmed. The Privacy Policy currently references an intent to comply with the DPF; whether an actual, current self-certification is on file with the U.S. Department of Commerce has not been checked against the public list at dataprivacyframework.gov. Do not represent this as an active certification until that\'s confirmed.',
              ],
              [
                '23Controls™ governance layer',
                'Described elsewhere in our own technical documentation as designed to align with NIST AI risk-management principles. This is an architectural design goal, not an independently audited NIST certification.',
              ],
            ],
          },
        },
        {
          heading: 'Security Practices',
          paragraphs: ['Consistent with what\'s described in our Privacy Policy, our security practices include:'],
          list: [
            'Encryption in transit (TLS) and at rest (AES-256) for Platform data.',
            'Multi-factor authentication and role-based access controls.',
            'Regular internal security review of the Platform and its infrastructure.',
            'Incident response procedures for suspected security events.',
          ],
          note: 'This list mirrors the Privacy Policy\'s Data Security section rather than introducing new claims, so the two pages don\'t drift apart — if one is updated, check the other.',
        },
        {
          heading: 'Reporting a Security Issue',
          paragraphs: [
            'If you believe you\'ve found a security vulnerability in our Services, please report it to security@synthetiq.io before disclosing it publicly. We ask that you give us a reasonable opportunity to investigate and address the issue before any public disclosure.',
          ],
          note: 'This is a standard, low-risk responsible-disclosure line, not a formal bug-bounty program — create the security@synthetiq.io mailbox (or point it at wherever these reports should actually land) before this page goes live, same as the legal@ and privacy@ addresses flagged on the other legal pages.',
        },
        {
          heading: 'Questions',
          paragraphs: [
            'SynthetIQ Signals®, Inc.',
            'Email: privacy@synthetiq.io',
          ],
        },
      ]}
    />
  )
}
